import { useLocation } from 'react-router-dom'
import { useActiveSpecialLecture } from './useActiveSpecialLecture'
import type { SpecialLectureLockView } from '@/api/unitAssessment'

export type SpecialLectureFlowResult = { redirect: string } | null

export interface UseSpecialLectureFlowOptions {
  enabled?: boolean
}

const SPECIAL_LECTURE_POLL_INTERVAL_MS = 10_000
const SPECIAL_LECTURE_ROUTE_PREFIX = '/my/special-lecture/'
const TODAY_PATH = '/my/today'

/** active 특강 status/mode → 진행 세그먼트. */
export function specialLectureSegment(lock: SpecialLectureLockView): 'online' | 'review' | 'wait' {
  if (lock.status === 'REVIEW_OPEN') return 'review'
  if (lock.status === 'SUBMITTED' || lock.status === 'GRADED') return 'wait'
  return lock.mode === 'ONLINE' ? 'online' : 'wait'
}

/** 특강 잠금 상태에 맞는 학습자 진행 경로. */
export function specialLecturePath(lock: SpecialLectureLockView): string {
  return `/my/special-lecture/${lock.id}/${specialLectureSegment(lock)}`
}

/**
 * active 특강과 현재 경로를 대조한다.
 *
 * terminal(COMPLETED/CANCELLED) 특강은 active API에서 null이므로, 특강 URL에 남아 있으면
 * 오늘 수업으로 이탈시킨다. 다른 active 특강/상태가 있으면 그 특강의 현재 세그먼트로 교정한다.
 */
export function specialLectureRedirect(
  path: string,
  lock: SpecialLectureLockView | null,
): string | null {
  if (!lock) return path.startsWith(SPECIAL_LECTURE_ROUTE_PREFIX) ? TODAY_PATH : null
  const expected = specialLecturePath(lock)
  return path === expected ? null : expected
}

/**
 * 단원평가 특강 hard lock — active 특강이 있으면 특강 진행 경로로 redirect(엣지3).
 *
 * - Hook 은 early return 이전에 호출(React Hook 규칙).
 * - fail-open: loading/error/null 이면 null → 학습자 정상 사용.
 * - COMPLETED/CANCELLED 는 서버에서 active 에서 제외 → 특강 URL이면 오늘 수업으로 이탈.
 * - 우선순위: ProtectedRoute 에서 diagnostic/preview/test flow 보다 먼저.
 */
export function useSpecialLectureFlow(
  options: UseSpecialLectureFlowOptions = {},
): SpecialLectureFlowResult {
  const { enabled = true } = options
  const path = useLocation().pathname
  const { data, isLoading, isError } = useActiveSpecialLecture({
    enabled,
    pollInterval: SPECIAL_LECTURE_POLL_INTERVAL_MS,
  })

  if (!enabled) return null
  if (isLoading || isError) return null
  const lock = data ?? null
  const redirect = specialLectureRedirect(path, lock)
  return redirect ? { redirect } : null
}
